import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useSnackbar } from "notistack";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { get_job_postings_api, update_job_posting_api } from "../../DAL/Careers/Careers";

const getStatusChipSx = (status) => {
  switch (status) {
    case "open": return { backgroundColor: "rgba(22,163,74,0.1)", color: "#16a34a" };
    case "closed": return { backgroundColor: "rgba(192,30,46,0.1)", color: "#c01e2e" };
    default: return { backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706" };
  }
};

export default function JobPostingsList() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    getJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getJobs = async () => {
    try {
      const result = await get_job_postings_api();
      if (result.code === 200) {
        setJobs(result.data || []);
      } else {
        enqueueSnackbar(result.message || "Failed to load job postings", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching job postings", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStatus = async (job) => {
    const newStatus = job.status === "open" ? "closed" : "open";
    const result = await update_job_posting_api(job._id, { status: newStatus });
    if (result.code === 200) {
      setJobs((prev) => prev.map((j) => (j._id === job._id ? { ...j, status: newStatus } : j)));
      enqueueSnackbar(`Role ${newStatus === "open" ? "opened" : "closed"}`, { variant: "success" });
    } else {
      enqueueSnackbar(result.message || "Failed to update role", { variant: "error" });
    }
  };

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress sx={{ color: "#c01e2e" }} />
        </Box>
      ) : (
        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 48px)" }}>
          <Paper sx={{ backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pb: 2.5, borderBottom: "1px solid rgba(26,26,26,0.09)" }}>
              <Box>
                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
                  Careers — Job Postings
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mt: 0.25 }}>
                  {jobs.length} roles
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  component={RouterLink}
                  to="/careers/applications"
                  variant="outlined"
                  startIcon={<PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", borderRadius: "8px",
                    "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" } }}
                >
                  View Applications
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate("/careers/jobs/add")}
                  sx={{ backgroundColor: "#c01e2e", color: "#fff", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, borderRadius: "8px", px: 2.5, boxShadow: "none",
                    "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
                >
                  Add Job
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Title", "Department", "Location", "Salary", "Status", "Actions"].map((label) => (
                      <TableCell key={label} sx={{ color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif", borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4" }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job._id} sx={{ "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}>
                      <TableCell sx={{ color: "#1a1a1a", fontSize: "14px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>{job.title}</TableCell>
                      <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{job.department}</TableCell>
                      <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{job.location}</TableCell>
                      <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                        £{Number(job.salaryMin).toLocaleString()} – £{Number(job.salaryMax).toLocaleString()}{job.salaryNote ? ` ${job.salaryNote}` : ""}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          size="small"
                          onClick={() => toggleStatus(job)}
                          sx={{ ...getStatusChipSx(job.status), borderRadius: "6px", fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", textTransform: "capitalize", cursor: "pointer" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/careers/jobs/edit/${job._id}`)} sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {jobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: "center", color: "#aaa", fontFamily: "'Rubik', sans-serif", py: 4 }}>
                        No job postings yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </>
  );
}